/**
 * Golden fixed-tick trace recorder.
 *
 * This deliberately knows no clocks, random sources, Three objects, or Rapier
 * objects. The integrator passes every fact it records, which keeps a trace a
 * record of the tick rather than a second, partially-hidden simulation path.
 */

import type { Action, Input } from '@/core/input';
import { moveBasisFromYaw, type MoveBasis } from '@/camera/moveBasis';

export type TraceKind = 'exact' | 'noisy';

export interface TraceTolerance {
  /** Numeric comparison band, in the unit named below. */
  readonly value: number;
  readonly unit: 'absolute' | 'tick-index';
  /** Why this field is allowed to vary, rather than silently weakening a gate. */
  readonly reason: string;
}

export interface TraceColumn {
  readonly name: string;
  readonly kind: TraceKind;
  readonly tolerance?: TraceTolerance;
}

export type TraceValue = boolean | number | string | null | readonly string[];
export type TraceRow = readonly TraceValue[];

export interface TraceBodyTransform {
  readonly handle: number;
  readonly translation: { readonly x: number; readonly y: number; readonly z: number };
  readonly rotation: { readonly x: number; readonly y: number; readonly z: number; readonly w: number };
}

export interface TraceSnapshot {
  readonly simTime: number;
  readonly paused: boolean;
  readonly muted: boolean;
  readonly character: {
    readonly x: number; readonly y: number; readonly z: number;
    readonly facing: number;
    readonly speed: number;
    readonly velocityY: number;
    readonly gait: string;
    readonly grounded: boolean;
    readonly airborneTime: number;
    readonly landingImpact: number;
    readonly turnRate: number;
    readonly slopeGrade: number;
    readonly scramble: number;
  };
  readonly basis: { readonly forwardX: number; readonly forwardZ: number; readonly rightX: number; readonly rightZ: number };
  readonly moveAxis: { readonly x: number; readonly y: number };
  readonly down: readonly string[];
  readonly pressed: readonly string[];
  readonly interaction: { readonly actId: string | null; readonly observeId: string | null; readonly progress: number };
  readonly story: {
    readonly stageId: string | null;
    readonly stageIndex: number | null;
    readonly stageTime: number | null;
    readonly cuesEmitted: number | null;
    readonly finished: boolean | null;
  };
  readonly cues: readonly string[];
  readonly bodies: readonly TraceBodyTransform[];
  readonly bodyCount: number;
  readonly colliderCount: number;
  readonly siblingPresent: boolean;
}

export interface TraceDocument {
  readonly meta: {
    readonly format: 'rosalie-golden-tick-trace-v1';
    readonly maxTicks: number;
    readonly script: TraceScriptName;
    readonly firstBodyTransforms: readonly TraceBodyTransform[];
    readonly lastBodyTransforms: readonly TraceBodyTransform[];
    readonly [key: string]: unknown;
  };
  readonly columns: readonly TraceColumn[];
  readonly rows: readonly TraceRow[];
}

export type TraceExternalEvent =
  | {
      readonly tick: number;
      readonly kind: 'teleportPlayer';
      readonly position: { readonly x: number; readonly y: number; readonly z: number };
      readonly facing: number;
    }
  | {
      readonly tick: number;
      readonly kind: 'bornSiblingTaken';
    };

export interface TraceOptions {
  readonly maxTicks: number;
  readonly script: TraceScriptName;
  readonly meta?: Readonly<Record<string, string | number | boolean | null>>;
}

export interface TraceStatus {
  readonly recording: boolean;
  readonly ticks: number;
  readonly complete: boolean;
}

export interface TraceScriptStep {
  readonly tick: number;
  readonly down?: readonly Action[];
  readonly up?: readonly Action[];
  /**
   * Scripts use button actions today, but clearing the analogue seam at tick
   * zero makes that fact explicit: a browser touch axis cannot leak into a
   * supposedly authored trace.
   */
  readonly axis?: { readonly x: number; readonly y: number; readonly active: boolean };
  /** Movement-basis yaw, held from this step until a later yaw replaces it. */
  readonly yaw?: number;
}

export const TRACE_ACTIONS = [
  'moveForward',
  'moveBack',
  'moveLeft',
  'moveRight',
  'sneak',
  'roll',
  'interact',
  'observe',
  'callHolly',
  'pause',
  'muteAudio',
] as const satisfies readonly Action[];

/**
 * Authored traces are data rather than one-off test code so replay can consume
 * the exact same edge stream later (D-045).
 */
export const TRACE_SCRIPTS = {
  none: [
    { tick: 0, axis: { x: 0, y: 0, active: false } },
  ],
  drive: [
    { tick: 0, axis: { x: 0, y: 0, active: false } },
    // ── Timed against the room, not against a round number ──────────────────
    //
    // The first version of this script held forward for 180 ticks and then
    // asked for a roll. Reading the recorded trace rather than the script
    // showed what actually happened: she reached the grey-box room's geometry
    // at **tick 127** and the gait fell back to `still` with the key still
    // down, so the roll segment was requested from a standstill against a wall
    // and **no roll ever occurred** — the trace was green, reproducible, and
    // silently covered one gait fewer than it claimed.
    //
    // So the roll is now asked for while she is demonstrably trotting, and the
    // walk into the wall is kept afterwards on purpose: collide-and-slide is
    // its own path through the tick and is worth holding still as well.
    { tick: 60, down: ['moveForward'] },
    { tick: 90, down: ['roll'] },
    { tick: 150, up: ['roll'] },
    { tick: 200, up: ['moveForward'] },
    // ── Why every hold in this script is FORWARD, and this is the finding ────
    //
    // The second segment was `moveBack` and the recorded trace stopped being
    // reproducible at **exactly tick 241**, the first tick of that hold: ticks
    // 0..240 were byte-identical across two captures — walk, trot, roll and a
    // wall press included — and every tick from 241 on differed, starting in
    // the `basis.*` columns.
    //
    // That is D-045's presentation→simulation writeback, measured instead of
    // argued. Backwards (or sideways) movement makes her FACING diverge from the
    // camera yaw; the idle auto-recentre then chases her facing on the RENDER
    // step, at display rate; `fixedUpdate` samples that yaw as its movement
    // basis. So identical fixed-step input produces different world-space
    // movement between two runs of the same script.
    //
    // The rule this leaves for anyone extending scripts WITHOUT a basis track:
    // **a golden trace may only contain movement that keeps her facing aligned
    // with the camera yaw** — in practice, forward-only from a settled start.
    // Anything else is a legitimate thing to record and is not a thing to
    // compare byte for byte until the trace also feeds the basis back in on
    // replay. A script that needs it declares `yaw`; `movementBasis()` then
    // supplies that authored basis on every following tick.
    { tick: 240, down: ['moveForward'] },
    { tick: 330, up: ['moveForward'] },
    // Tick 420 pauses. Ticks 421..480 are deliberately a sixty-tick pause: the
    // game clock must not advance across them and `input.consume()` must still
    // run, which is exactly what an extraction can drop without any test
    // noticing.
    { tick: 420, down: ['pause'] },
    { tick: 421, up: ['pause'] },
    { tick: 481, down: ['pause'] },
    { tick: 482, up: ['pause'] },
    { tick: 520, down: ['muteAudio'] },
    { tick: 521, up: ['muteAudio'] },
    { tick: 560, down: ['sneak', 'moveForward'] },
    { tick: 700, up: ['sneak', 'moveForward'] },
  ],
  reverse: [
    { tick: 0, axis: { x: 0, y: 0, active: false }, yaw: 0 },
    // The test room boot yaw is 0: forward is +Z, and the wall at z = 6 makes
    // its open side the useful place to exercise the first three reverse holds.
    { tick: 60, down: ['moveBack'] },
    { tick: 150, up: ['moveBack'], down: ['moveLeft'] },
    { tick: 250, up: ['moveLeft'], down: ['moveRight'] },
    { tick: 350, up: ['moveRight'], yaw: -Math.PI / 2, down: ['moveForward'] },
    { tick: 440, up: ['moveForward'], down: ['moveBack', 'roll'] },
    { tick: 500, up: ['roll'] },
    { tick: 550, up: ['moveBack'] },
  ],
} as const satisfies Readonly<Record<string, readonly TraceScriptStep[]>>;

export type TraceScriptName = keyof typeof TRACE_SCRIPTS;

/** A capture is evidence, not an unbounded in-memory telemetry stream. */
export const MAX_TRACE_TICKS = 10_000;

const COLUMNS: readonly TraceColumn[] = [
  exact('tick'),
  exact('simTime'),
  exact('paused'),
  exact('muted'),
  exact('character.x'),
  exact('character.y'),
  exact('character.z'),
  exact('character.facing'),
  exact('character.speed'),
  exact('character.velocityY'),
  exact('character.gait'),
  exact('character.grounded'),
  exact('character.airborneTime'),
  exact('character.landingImpact'),
  exact('character.turnRate'),
  exact('character.slopeGrade'),
  exact('character.scramble'),
  exact('basis.forwardX'),
  exact('basis.forwardZ'),
  exact('basis.rightX'),
  exact('basis.rightZ'),
  exact('moveAxis.x'),
  exact('moveAxis.y'),
  exact('actions.down'),
  exact('actions.pressed'),
  exact('interaction.actId'),
  exact('interaction.observeId'),
  exact('interaction.progress'),
  exact('story.stageId'),
  exact('story.stageIndex'),
  exact('story.stageTime'),
  exact('story.cuesEmitted'),
  exact('story.finished'),
  exact('cues'),
  exact('physics.bodyCount'),
  exact('physics.colliderCount'),
  exact('physics.bodyDigest'),
  {
    name: 'siblingPresent',
    kind: 'noisy',
    tolerance: {
      value: 1,
      unit: 'tick-index',
      reason: 'Only the tick index of this render-rate-originated removal may vary; it is constant across both committed trace windows.',
    },
  },
];

/** One boolean read is the entire per-tick cost while recording is off. */
export class TickTrace {
  #recording = false;
  #complete = false;
  #maxTicks = 0;
  #script: TraceScriptName = 'none';
  #rows: TraceRow[] = [];
  #firstSimTime: number | null = null;
  #meta: Readonly<Record<string, string | number | boolean | null>> = {};
  #firstBodyTransforms: TraceBodyTransform[] = [];
  #lastBodyTransforms: TraceBodyTransform[] = [];
  #movementYaw: number | null = null;
  #externalEvents: TraceExternalEvent[] = [];

  arm(options: TraceOptions): void {
    if (!Number.isSafeInteger(options.maxTicks) || options.maxTicks <= 0 || options.maxTicks > MAX_TRACE_TICKS) {
      throw new Error(
        `trace maxTicks must be a positive safe integer no greater than ${String(MAX_TRACE_TICKS)}, got ${String(options.maxTicks)}`,
      );
    }
    const script: readonly TraceScriptStep[] | undefined = TRACE_SCRIPTS[options.script];
    if (script === undefined) throw new Error(`trace.arm: unknown script "${String(options.script)}"`);
    for (const step of script) {
      if (!Number.isSafeInteger(step.tick) || step.tick < 0) {
        throw new Error(`trace.arm: script "${options.script}" tick ${String(step.tick)} must be a non-negative safe integer`);
      }
      if (step.yaw !== undefined && !Number.isFinite(step.yaw)) {
        throw new Error(`trace.arm: script "${options.script}" tick ${String(step.tick)} has non-finite yaw ${String(step.yaw)}`);
      }
    }
    for (let index = 1; index < script.length; index += 1) {
      const previous = script[index - 1];
      const current = script[index];
      if (previous === undefined || current === undefined) continue;
      if (current.tick < previous.tick) {
        throw new Error(`trace.arm: script "${options.script}" tick ${String(current.tick)} follows tick ${String(previous.tick)}`);
      }
    }
    this.#maxTicks = options.maxTicks;
    this.#script = options.script;
    this.#rows = [];
    this.#firstSimTime = null;
    this.#firstBodyTransforms = [];
    this.#lastBodyTransforms = [];
    this.#movementYaw = null;
    this.#externalEvents = [];
    this.#meta = options.meta ?? {};
    this.#complete = false;
    this.#recording = false;
  }

  start(): void {
    if (this.#maxTicks <= 0) throw new Error('trace.start() requires trace.arm() first');
    this.#recording = true;
  }

  stop(): void {
    this.#recording = false;
  }

  get status(): TraceStatus {
    return { recording: this.#recording, ticks: this.#rows.length, complete: this.#complete };
  }

  /**
   * Apply authored edges through Input's existing touch seams. The caller uses
   * the returned flag to avoid building a snapshot while this recorder is off.
   */
  drive(input: Input, tick: number): TickTrace | null {
    if (!this.#recording) return null;
    const script: readonly TraceScriptStep[] = TRACE_SCRIPTS[this.#script];
    for (const step of script) {
      if (step.tick !== tick) continue;
      if (step.axis) input.setTouchAxis(step.axis.x, step.axis.y, step.axis.active);
      if (step.yaw !== undefined) this.#movementYaw = step.yaw;
      for (const action of step.down ?? []) input.setTouchAction(action, true);
      for (const action of step.up ?? []) input.setTouchAction(action, false);
    }
    return this;
  }

  /** Writes the authored replay basis only while an armed script has supplied one. */
  movementBasis(out: MoveBasis): boolean {
    if (!this.#recording || this.#movementYaw === null) return false;
    moveBasisFromYaw(this.#movementYaw, out);
    return true;
  }

  /** Records an external request at the fixed tick where the simulation applied it. */
  recordExternalEvent(event: TraceExternalEvent): void {
    if (!this.#recording) return;
    this.#externalEvents.push(event);
  }

  record(tick: number, snapshot: TraceSnapshot): void {
    if (!this.#recording) return;
    if (tick !== this.#rows.length) {
      throw new Error(`trace tick ${String(tick)} does not follow row ${String(this.#rows.length)}`);
    }

    const bodies = snapshot.bodies
      .map((body) => quantizedBody(body, 1_000_000))
      .sort((a, b) => a.handle - b.handle);
    if (this.#firstSimTime === null) {
      this.#firstSimTime = snapshot.simTime;
      this.#firstBodyTransforms = bodies;
    }
    this.#lastBodyTransforms = bodies;

    const c = snapshot.character;
    const row: TraceRow = [
      tick,
      quantize(snapshot.simTime - this.#firstSimTime),
      snapshot.paused,
      snapshot.muted,
      quantize(c.x), quantize(c.y), quantize(c.z), quantize(c.facing), quantize(c.speed), quantize(c.velocityY),
      c.gait, c.grounded, quantize(c.airborneTime), quantize(c.landingImpact), quantize(c.turnRate), quantize(c.slopeGrade), quantize(c.scramble),
      quantize(snapshot.basis.forwardX), quantize(snapshot.basis.forwardZ), quantize(snapshot.basis.rightX), quantize(snapshot.basis.rightZ),
      quantize(snapshot.moveAxis.x), quantize(snapshot.moveAxis.y),
      [...snapshot.down], [...snapshot.pressed],
      snapshot.interaction.actId, snapshot.interaction.observeId, quantize(snapshot.interaction.progress),
      snapshot.story.stageId, snapshot.story.stageIndex, nullableQuantize(snapshot.story.stageTime), snapshot.story.cuesEmitted, snapshot.story.finished,
      [...snapshot.cues],
      snapshot.bodyCount, snapshot.colliderCount,
      bodyDigest(bodies.map((body) => quantizedBody(body, 10_000))), snapshot.siblingPresent,
    ];
    this.#rows.push(row);
    if (this.#rows.length >= this.#maxTicks) {
      this.#recording = false;
      this.#complete = true;
    }
  }

  dump(): TraceDocument {
    return {
      meta: {
        format: 'rosalie-golden-tick-trace-v1',
        maxTicks: this.#maxTicks,
        script: this.#script,
        ...this.#meta,
        externalEvents: this.#externalEvents,
        firstBodyTransforms: this.#firstBodyTransforms,
        lastBodyTransforms: this.#lastBodyTransforms,
      },
      columns: COLUMNS,
      rows: this.#rows,
    };
  }

  /** Rows stay one-per-line so a golden change names its tick in a git diff. */
  serialize(): string {
    return serializeTrace(this.dump());
  }
}

export function serializeTrace(document: TraceDocument): string {
  const rows = document.rows.map((row) => `    ${JSON.stringify(row)}`).join(',\n');
  return `{\n  "meta": ${JSON.stringify(document.meta, null, 2)},\n  "columns": ${JSON.stringify(document.columns, null, 2)},\n  "rows": [\n${rows}\n  ]\n}\n`;
}

function exact(name: string): TraceColumn {
  return { name, kind: 'exact' };
}

function quantize(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000;
}

function nullableQuantize(value: number | null): number | null {
  return value === null ? null : quantize(value);
}

function quantizedBody(body: TraceBodyTransform, scale: number): TraceBodyTransform {
  return {
    handle: body.handle,
    translation: {
      x: quantizeTo(body.translation.x, scale),
      y: quantizeTo(body.translation.y, scale),
      z: quantizeTo(body.translation.z, scale),
    },
    rotation: {
      x: quantizeTo(body.rotation.x, scale),
      y: quantizeTo(body.rotation.y, scale),
      z: quantizeTo(body.rotation.z, scale),
      w: quantizeTo(body.rotation.w, scale),
    },
  };
}

function quantizeTo(value: number, scale: number): number {
  return Math.round(value * scale) / scale;
}

/** Small FNV-1a, intentionally inline so the digest has no dependency surface. */
function bodyDigest(bodies: readonly TraceBodyTransform[]): string {
  let hash = 0x811c9dc5;
  const write = (text: string): void => {
    for (let i = 0; i < text.length; i += 1) {
      hash ^= text.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
  };
  for (const body of bodies) {
    const { translation: t, rotation: r } = body;
    write(`${body.handle}|${t.x}|${t.y}|${t.z}|${r.x}|${r.y}|${r.z}|${r.w};`);
  }
  return hash.toString(16).padStart(8, '0');
}
